import { AsyncStorage } from "react-native";
import AWS from "aws-sdk/dist/aws-sdk-react-native";
import {
	CognitoUserPool,
	AuthenticationDetails,
	CognitoUser,
	CognitoUserAttribute,
	CognitoRefreshToken,
} from "amazon-cognito-identity-js";

import { Storage } from "utils/storage";
import { COGNITO } from "utils/config";
import MemoryStorage from "utils/MemoryStorage";

const poolData = {
	UserPoolId: COGNITO.UserPoolId, // Your user pool id here
	ClientId: COGNITO.ClientId, // Your client id here
};

export const userPool = new CognitoUserPool(poolData);

export async function signout() {
	let storage = new Storage();
	const keyPrefix = `CognitoIdentityServiceProvider.${userPool.getClientId()}`;
	const lastUserKey = `${keyPrefix}.LastAuthUser`;
	const username = await storage.getItem(lastUserKey);

	const keyPrefixRe = `CognitoIdentityServiceProvider.${userPool.getClientId()}.${
		username
		}`;

	const accessTokenKey = `${keyPrefixRe}.accessToken`;

	const accessValue = await storage.getItem(accessTokenKey);

	if (!username || !accessValue) {
		storage.removeLogin();
		storage.removeRefresh();
		return Promise.resolve("signed out");
	}

	const userPools = new CognitoUserPool(poolData);

	const userData = {
		Username: username,
		Pool: userPools,
	};
	const cognitoUser = new CognitoUser(userData);

	try {
		cognitoUser.signOut();
		storage.signOut();
		storage.removeLogin();
		return Promise.resolve("signed out");
	} catch (err) {
		return Promise.reject(err);
	}
}

export function login(username, password) {
	let storage = new Storage();
	if (!username) {
		return Promise.reject("No username");
	}

	const authenticationData = {
		Username: username,
		Password: password,
	};

	const authenticationDetails = new AuthenticationDetails(authenticationData);
	const userPool = new CognitoUserPool(poolData);
	const mem = MemoryStorage;
	const userData = {
		Username: authenticationData.Username,
		Pool: userPool,
		Storage: mem,
	};
	const cognitoUser = new CognitoUser(userData);

	return new Promise((resolve, reject) => {
		cognitoUser.authenticateUser(authenticationDetails, {
			onSuccess(resp) {
				const keyPrefix = `CognitoIdentityServiceProvider.${userPool.getClientId()}.${
					username
					}`;
				const idTokenKey = `${keyPrefix}.idToken`;
				const accessTokenKey = `${keyPrefix}.accessToken`;
				const deviceKey = `${keyPrefix}.deviceKey`;
				const refreshTokenKey = `${keyPrefix}.refreshToken`;
				const lastUserKey = `CognitoIdentityServiceProvider.${userPool.getClientId()}.LastAuthUser`;

				cognitoUser.getUserAttributes(function(err, result) {
					if (err) {
						alert(err.message || JSON.stringify(err));
						return;
					}
					for (let i = 0; i < result.length; i++) {
						storage.setItem(result[i].getName(), result[i].getValue());
						//console.log('attribute ' + result[i].getName() + ' has value ' + result[i].getValue());
					}
				});

				cognitoUser.getDevice({
					onSuccess: res => {
						storage.setItem(deviceKey, res.Device.DeviceKey);
						storage.setItem("deviceKey", res.Device.DeviceKey);
						storage.setUUID();
					},
					onFailure: err => {
						console.log(err);
					},
				});

				storage.setLogin();
				storage.setItem(idTokenKey, resp.getIdToken().getJwtToken());
				storage.setItem(
					accessTokenKey,
					resp.getAccessToken().getJwtToken()
				);
				storage.setItem(
					refreshTokenKey,
					resp.getRefreshToken().getToken()
				);
				storage.setItem("refreshValue", resp.getRefreshToken().getToken());
				storage.setItem(lastUserKey, username);

				resolve(resp);
			},
			onFailure(err) {
				reject(err);
			},
		});
	});
}

export async function refresh() {
	let storage = new Storage();

	const keyPrefix = `CognitoIdentityServiceProvider.${userPool.getClientId()}`;
	const lastUserKey = `${keyPrefix}.LastAuthUser`;
	const lastAuthUser = await storage.getItem(lastUserKey);

	const keyPrefixRe = `CognitoIdentityServiceProvider.${userPool.getClientId()}.${
		lastAuthUser
		}`;

	const idTokenKey = `${keyPrefixRe}.idToken`;
	const accessTokenKey = `${keyPrefixRe}.accessToken`;
	const deviceKey = `${keyPrefixRe}.deviceKey`;
	const refreshTokenKey = `${keyPrefixRe}.refreshToken`;

	const refreshValue = await storage.getItem("refreshValue");
	const deviceValue = await storage.getItem("deviceKey");

	var refreshParams = {
		ClientId: COGNITO.ClientId,
		AuthFlow: "REFRESH_TOKEN_AUTH",
		AuthParameters: {
			USERNAME: lastAuthUser,
			REFRESH_TOKEN: refreshValue,
			DEVICE_KEY: deviceValue,
		},
	};

	return new Promise((resolve, reject) => {
		userPool.client.makeUnauthenticatedRequest(
			"initiateAuth",
			refreshParams,
			(err, authResult) => {
				if (err) {
					reject(err);
					return;
				}
				if (authResult) {
					const authenticationResult = authResult.AuthenticationResult;
					storage.setItem(idTokenKey, authenticationResult.IdToken);
					storage.setItem(
						accessTokenKey,
						authenticationResult.AccessToken
					);
					if (authenticationResult.RefreshToken) {
						storage.setItem(refreshTokenKey, authenticationResult.RefreshToken);
						storage.setItem("refreshValue", authenticationResult.RefreshToken);
					}

					storage.setUUID();
					resolve(authResult);
					return;
				}

				reject(authResult);
				return;
			}
		);
	}).catch(err => {
		return Promise.reject(err);
	});
}

async function getCurrentUser() {
	let storage = new Storage();

	const keyPrefix = `CognitoIdentityServiceProvider.${userPool.getClientId()}`;
	const lastUserKey = `${keyPrefix}.LastAuthUser`;
	const lastAuthUser = await storage.getItem(lastUserKey);

	const keyPrefixRe = `CognitoIdentityServiceProvider.${userPool.getClientId()}.${
		lastAuthUser
		}`;

	const idTokenKey = `${keyPrefixRe}.idToken`;
	const accessTokenKey = `${keyPrefixRe}.accessToken`;
	const refreshTokenKey = `${keyPrefixRe}.refreshToken`;

	const idValue = await storage.getItem(idTokenKey);
	const accessValue = await storage.getItem(accessTokenKey);
	const refreshValue = await storage.getItem(refreshTokenKey);

	const store = {
		[idTokenKey]: idValue,
		[accessTokenKey]: accessValue,
		[refreshTokenKey]: refreshValue,
		[lastUserKey]: lastAuthUser,
	};

	const mem = MemoryStorage;
	mem.setItem("idToken", idValue);
	mem.setData(store);

	if (lastAuthUser) {
		const cognitoUser = {
			Username: lastAuthUser,
			Pool: userPool,
			Storage: mem,
		};

		return Promise.resolve(new CognitoUser(cognitoUser));
	} else {
		return Promise.reject("no user found");
	}
}

export async function updateUser(newUserData) {
	let attributeList = [];
	let storage = new Storage();

	// convert to amazon user object
	Object.keys(newUserData).forEach(function(key) {
			let attribute = {
				Name : key,
				Value : newUserData[key]
			};
			attribute = new CognitoUserAttribute(attribute);
			attributeList.push(attribute);
	});
 
	// get current user data
	let userPool = new CognitoUserPool(poolData);
    let cognitoUser = userPool.getCurrentUser();

	// update!
	return new Promise((resolve, reject) => {
		if (cognitoUser != null) {
			cognitoUser.getSession(function(err, session) {
				if (err) {
					reject(err.message || JSON.stringify(err));
				}
				console.log('session validity: ' + session.isValid());
	 
				cognitoUser.updateAttributes(attributeList, function(err, result) {
					if (err) {
						reject(err.message || JSON.stringify(err));
					}
					console.log('call result: ' + result);
	
					cognitoUser.getUserAttributes(function(err, result) {
						if (err) {
							reject(err.message || JSON.stringify(err));
						}
						for (let i = 0; i < result.length; i++) {
							storage.setItem(result[i].getName(), result[i].getValue());
						}
	
						resolve(true);
					});
				});
			});
		}
	});
    
}

export async function autoLogin() {
	let storage = new Storage();
	const keyPrefix = `CognitoIdentityServiceProvider.${userPool.getClientId()}`;
	const lastUserKey = `${keyPrefix}.LastAuthUser`;

	const lastAuthUser = await storage.getItem(lastUserKey);

	const keyPrefixRe = `CognitoIdentityServiceProvider.${userPool.getClientId()}.${
		lastAuthUser
		}`;

	const idTokenKey = `${keyPrefixRe}.idToken`;
	const accessTokenKey = `${keyPrefixRe}.accessToken`;
	const refreshTokenKey = `${keyPrefixRe}.refreshToken`;
	const acctoken = await storage.getItem(accessTokenKey);

	if (!lastAuthUser || !acctoken) {
		return Promise.reject("No valid session");
	}


	return getCurrentUser()
		.then(cognitoUser => {
			return new Promise((resolve, reject) => {
				cognitoUser.getSession((err, session) => {
					if (err) {
						reject(err);
						return;
					}
					if (session.isValid()) {
						console.log(session);
						storage.setLogin();
						storage.setItem(
							idTokenKey,
							session.getIdToken().getJwtToken()
						);
						storage.setItem(
							accessTokenKey,
							session.getAccessToken().getJwtToken()
						);
						storage.setItem(
							refreshTokenKey,
							session.getRefreshToken().getToken()
						);
						storage.setItem("refreshValue", session.getRefreshToken().getToken());
						storage.setItem(lastUserKey, lastAuthUser);

						storage.setUUID();
						resolve(session);
					} else {
						reject("No valid session");
					}
				});
			});
		})
		.catch(err => {
			console.log(err);
			return Promise.reject(err);
		});
}

export function resetPassword(username) {
	if (!username) {
		return Promise.reject("No username");
	}

	const userData = {
		Username: username,
		Pool: userPool,
	};
	const cognitoUser = new CognitoUser(userData);

	return new Promise((resolve, reject) => {
		cognitoUser.forgotPassword({
			onSuccess() {
				resolve();
			},
			onFailure(err) {
				reject(err);
			},
		});
	});
}

export function confirmForgotPassword(username, verificationCode, newPassword) {
	if (!username) {
		return Promise.reject("No username");
	}

	const cognitoUser = new CognitoUser({
		Username: username,
		Pool: userPool,
	});

	return new Promise((resolve, reject) => {
		cognitoUser.confirmPassword(verificationCode, newPassword, {
			onSuccess() {
				resolve();
			},
			onFailure(err) {
				const error = {};
				error.error = err;
				if (err.message) {
					error.message = consumeErrorMessage(err.message);
				}
				reject(error);
			},
		});
	});
}

export function signUp(email, password, attributeList) {
	return new Promise((resolve, reject) => {
		userPool.signUp(email, password, attributeList, null, (err, result) => {
			if (err) {
				const error = {};
				error.error = err;
				if (err.message) {
					error.message = consumeErrorMessage(err.message);
				}
				return reject(error);
			}
			return resolve(result);
		});
	});
}

export function consumeErrorMessage(message) {
	switch (message) {
		case "Username/client id combination not found.":
			return "validation.emailNotFound";
		case "Invalid code provided, please request a code again.":
			return "validation.passwordResetExpired";
		case "An account with the given email already exists.":
			return { email: "validation.emailAlreadyExists" };
		case "Invalid phone number format.":
			return { mobileNumber: "validation.invalidPhoneNumberFormat" };
		default:
			return message;
	}
}



// WEBPACK FOOTER //
// ./src/utils/awsAuth.js